import { useCallback, useEffect, useRef, useState } from 'react';
import Big from 'big.js';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
    Combobox,
    CustomTable,
    CustomTableRef,
    ETypeOfNotification,
    GetItemsProps,
    InputDate,
    setNotification,
} from 'medere-ui-framework';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { getAllCompanies } from '../../servicesHooks/Accounting/useAccountingService';
import { getProfessionals } from '../../servicesHooks/Budget/useBudgetService';
import { getPaymentMethodsForCombo } from '../../services/SupplierController';
import { getHealthEntityComboOptionsWithAll } from '../../services/digitalRPservice';
import { getPendingItems, createProfessionalSettlement, getSettlementPaymentPreview } from '../../services/ProfessionalSettlementController';
import { INSUFFICIENT_BALANCE, parseApiError } from '../../services/apiError';
import { FinancerPendingDetailDTO, PaginatedFinancerPendingDetailsDTO, SelectedMovementDTO } from '../../dto/FinancerDistributionDTO';
import { ComboDataDTO, defaultAllComboDataDTO } from '../../dto/ComboDataDTO';
import { SettlementPaymentPreviewDTO } from '../../dto/ProfessionalSettlementDTO';
import { ManualAdjustmentDTO, ManualAdjustmentType } from '../../dto/HonorariumLiquidationDTO';
import ManualAdjustmentDialog from '../../components/ManualAdjustmentDialog/ManualAdjustmentDialog';
import { getDistributionContractTypeDescription } from '../../enums/EDistributionContractType';
import { EPayerType } from '../../enums/EPayerType';
import { formatMoney, sumMoney } from '../../utils/money';
import { findUnselectedNegatives, movementKey, toSelectedMovement } from '../../utils/honorariumMovement';
import MovementTypeTag from '../../components/HonorariumMovement/MovementTypeTag';
import SignedAmount from '../../components/HonorariumMovement/SignedAmount';
import RetentionLinesTable from '../../components/HonorariumMovement/RetentionLinesTable';
import AccountDebtLines from '../../components/HonorariumMovement/AccountDebtLines';
import FilterMultiSelect from '../FinancerDistribution/FilterMultiSelect';
import { FORM_STATE_OPTIONS, PAGE_SIZE, SelectOption, emptyToUndefined } from '../FinancerDistribution/financerDistributionFormat';
import { NO_OPEN_CASH_REGISTER_MESSAGE, usePaymentOrigin } from '../../hooks/usePaymentOrigin';
import PaymentOriginFields from '../../components/PaymentOriginFields/PaymentOriginFields';
import PaymentOriginNote from '../../components/PaymentOriginFields/PaymentOriginNote';
import { confirmAction } from '../../components/ConfirmDelete/confirmDelete';
import Kpi from '../../components/Kpi/Kpi';
import { formatDate, formatDateParam } from './settlementFormat';
import UnsettledNegativesDialog from './UnsettledNegativesDialog';

const ALL = -1;
/** Estado "Pagado" de la factura de la orden. */
const FORM_STATE_PAID = 3;
/** Estado "Facturada" de la orden. */
const BENEFIT_ORDER_STATE_BILLED = 2;
/**
 * Alcance fijo de la rendición: solo órdenes de paciente en estado Facturada (no se puede cambiar).
 * Las órdenes de paciente quedan en Facturada aunque su factura se pague. El backend también lo impone.
 */
const SETTLEMENT_PAYER_TYPES = [EPayerType.PATIENT];
const SETTLEMENT_ORDER_STATES = [BENEFIT_ORDER_STATE_BILLED];
/** Otro usuario rindió o pre-liquidó algún movimiento seleccionado mientras se armaba la rendición. */
const NOT_PENDING_ERROR_CODES = ['MOVEMENT_NOT_PENDING', 'NO_PENDING_MOVEMENTS'];
/** Las retenciones por facturación propia cambiaron desde la vista previa: hay que volver a revisar el pago. */
const RETENTIONS_CHANGED = 'RETENTIONS_CHANGED';

/** Opción del combo de entidad facturadora (getAllCompanies). */
interface BillingEntityOption {
    company: number;
    description: string;
}

/** Filtros de la última búsqueda: la grilla y la rendición usan estos, no los filtros sin aplicar. */
interface SettlementSearch {
    billingEntityId: number;
    professionalId: number;
    healthEntityId: number;
    fromDate: Date;
    toDate: Date;
    formStates: number[];
    paymentMethods: number[];
}

/** `movementKey` agrupa las filas que se seleccionan juntas; `rowKey` es único por fila. */
type PendingRow = FinancerPendingDetailDTO & { rowKey: string; movementKey: string };

/** Todo lo pendiente con los filtros, o solo los movimientos seleccionados. */
type SettlementScope = 'all' | 'selected';

/** Débito o crédito manual cargado en el paso de pago; `key` solo identifica la fila en la tabla. */
type AdjustmentRow = ManualAdjustmentDTO & { key: number };

/** Suma de los ajustes de un tipo, como texto con 2 decimales (lo que espera el backend). */
function adjustmentsTotal(rows: AdjustmentRow[], type: ManualAdjustmentType): string {
    return sumMoney(rows.filter(r => r.type === type).map(r => r.amount)).round(2).toFixed(2);
}

function formatHonorariumPercentage(row: PendingRow): string {
    if (row.distributionContractType === 2 || row.honorariumPercentage == null) return '—';
    return `${row.honorariumPercentage.toFixed(1)}%`;
}

interface NewProfessionalSettlementPanelProps {
    onClose: () => void;
    onSettlementCreated: () => void;
}

export default function NewProfessionalSettlementPanel({ onClose, onSettlementCreated }: NewProfessionalSettlementPanelProps) {
    const intl = useIntl();
    const dispatch = useDispatch();

    const [confirmStep, setConfirmStep] = useState(false);

    // Filtros (sin aplicar hasta "Buscar")
    const [selectedCompany, setSelectedCompany] = useState<BillingEntityOption | null>(null);
    const [selectedProfessional, setSelectedProfessional] = useState<ComboDataDTO | null>(null);
    const [selectedHealthEntity, setSelectedHealthEntity] = useState<ComboDataDTO>(defaultAllComboDataDTO);
    const [fromDate, setFromDate] = useState<Date | null>(new Date());
    const [toDate, setToDate] = useState<Date | null>(new Date());
    // Pagado por defecto: rinde solo lo cobrado. Se filtra por la factura porque la orden de paciente
    // queda en Facturada aunque su factura se pague
    const [formStates, setFormStates] = useState<number[]>([FORM_STATE_PAID]);
    const [paymentMethods, setPaymentMethods] = useState<number[]>([]);
    const [paymentMethodOptions, setPaymentMethodOptions] = useState<SelectOption[]>([]);

    // Grilla
    // La grilla carga de a páginas con el scroll (CustomTable): lee los filtros aplicados del ref
    const [appliedSearch, setAppliedSearch] = useState<SettlementSearch | null>(null);
    const appliedSearchRef = useRef<SettlementSearch | null>(null);
    const tableRef = useRef<CustomTableRef>(null);
    // Totales del servidor sobre todo el resultado: llegan con la primera página
    const [totals, setTotals] = useState<PaginatedFinancerPendingDetailsDTO | null>(null);
    // Filas ya cargadas por el scroll: para mostrar la selección, sumarla y detectar anulaciones sin seleccionar
    const seenRowsRef = useRef(new Map<string, PendingRow>());
    // Cambia con cada página cargada, para que la selección se vuelva a pintar sobre las filas nuevas
    const [, setLoadedPages] = useState(0);

    // Selección por clave de movimiento: sobrevive al cambio de página
    const [selection, setSelection] = useState<Map<string, SelectedMovementDTO>>(new Map());
    const [unselectedNegatives, setUnselectedNegatives] = useState<FinancerPendingDetailDTO[]>([]);
    const [scope, setScope] = useState<SettlementScope>('all');
    const [loadingClose, setLoadingClose] = useState(false);

    // Paso de pago
    const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
    const [paymentMethodForOrder, setPaymentMethodForOrder] = useState<ComboDataDTO | null>(null);
    const paymentOrigin = usePaymentOrigin({ enabled: confirmStep, paymentMethodId: paymentMethodForOrder?.id });
    const {
        cashRegister: selectedCashRegister,
        financialAccount: selectedFinancialAccount,
        balance: selectedFinancialAccountBalance,
        loadingBalance: loadingSelectedFinancialAccountBalance,
        cashRegistersStatus,
        requiresPaymentOrigin,
        // El sitio maneja cajas y el usuario no tiene ninguna abierta: no puede pagar (se previene, DS §3.8)
        missingOpenCashRegister,
    } = paymentOrigin;

    // Monto a pagar calculado por el backend: la parte del profesional menos lo que debe en la cuenta corriente
    const [paymentPreview, setPaymentPreview] = useState<SettlementPaymentPreviewDTO | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    // Solo cuenta la última vista previa pedida: si se vuelve atrás y se cambia la selección, la anterior se descarta
    const previewRequestRef = useRef(0);
    // Parte del profesional de lo que se rinde: la vista previa se vuelve a pedir con ella cuando cambian los ajustes
    const [previewBase, setPreviewBase] = useState<string | null>(null);

    // Débitos y créditos manuales de la rendición (se pierden con una nueva búsqueda)
    const [adjustments, setAdjustments] = useState<AdjustmentRow[]>([]);
    const [adjustmentDialogType, setAdjustmentDialogType] = useState<ManualAdjustmentType | null>(null);
    const adjustmentKeyRef = useRef(0);

    // ── Initial data loading ──

    useEffect(() => {
        getAllCompanies({ offSet: 0, pageSize: 2 } as GetItemsProps)
            .then((data: BillingEntityOption[] | { data?: BillingEntityOption[] }) => {
                const items = Array.isArray(data) ? data : data?.data ?? [];
                if (items.length === 1) setSelectedCompany(items[0]);
            })
            .catch((error: unknown) => parseApiError(error));
    }, []);

    useEffect(() => {
        getPaymentMethodsForCombo({ offSet: 0, pageSize: 200 } as GetItemsProps)
            .then((methods: ComboDataDTO[]) => setPaymentMethodOptions((methods ?? []).map(m => ({ label: m.name, value: m.id }))))
            .catch((error: unknown) => parseApiError(error));
    }, []);

    // ── Computed totals ──

    // Filas cargadas de los movimientos seleccionados (una clave puede tener varias filas)
    const selectedRows = Array.from(seenRowsRef.current.values()).filter(r => selection.has(r.movementKey));
    const selectedTotal = sumMoney(selectedRows.map(r => r.amount));
    const selectedProfessionalTotal = sumMoney(selectedRows.map(r => r.professionalAmount));
    const scopeRowCount = scope === 'all' ? totals?.totalCount ?? 0 : selectedRows.length;
    const scopeTotal: Big | number = scope === 'all' ? totals?.totalAmount ?? 0 : selectedTotal;
    const paymentHasInsufficientFunds = requiresPaymentOrigin
        && paymentAmount != null
        && selectedFinancialAccountBalance != null
        && selectedFinancialAccountBalance < paymentAmount;

    // ── Grilla ──

    const getPendingRows = useCallback(async (offset: number, pageSize: number): Promise<PendingRow[]> => {
        const search = appliedSearchRef.current;
        if (!search) return [];
        try {
            const data = await getPendingItems({
                billingEntityId: search.billingEntityId,
                professionalId: search.professionalId,
                fromDate: formatDateParam(search.fromDate),
                toDate: formatDateParam(search.toDate, true),
                healthEntityId: search.healthEntityId,
                payerTypes: SETTLEMENT_PAYER_TYPES,
                formStateTypes: emptyToUndefined(search.formStates),
                benefitOrderStates: SETTLEMENT_ORDER_STATES,
                paymentMethodIds: emptyToUndefined(search.paymentMethods),
                offset,
                pageSize,
            });
            if (offset === 0) {
                seenRowsRef.current = new Map();
                setTotals(data);
            }
            const pageRows: PendingRow[] = (data.items ?? []).map((item, i) => ({
                ...item,
                rowKey: `${movementKey(item)}#${offset + i}`,
                movementKey: movementKey(item),
            }));
            pageRows.forEach(r => seenRowsRef.current.set(r.rowKey, r));
            setLoadedPages(n => n + 1);
            return pageRows;
        } catch (error: unknown) {
            // CustomTable no captura el error: sin esto queda el spinner colgado
            parseApiError(error);
            dispatch(setNotification({
                title: 'No se pudieron cargar los ítems pendientes. Intentá de nuevo.',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return [];
        }
    }, [dispatch]);

    /** Nueva búsqueda o recarga: descarta la selección y vuelve a cargar desde la primera página. */
    function reloadGrid(search: SettlementSearch) {
        appliedSearchRef.current = search;
        setSelection(new Map());
        setAdjustments([]);
        setTotals(null);
        // La primera búsqueda monta la tabla, que carga sola; las siguientes la refrescan
        if (appliedSearch) tableRef.current?.refreshTable?.();
        setAppliedSearch(search);
    }

    function handleSearch() {
        if (!selectedCompany || !fromDate || !toDate || !selectedProfessional) {
            dispatch(setNotification({
                title: intl.formatMessage({ id: 'ThereAreMissingRequiredFields', defaultMessage: 'Completá los campos requeridos (Emisor y Profesional)' }),
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        const search: SettlementSearch = {
            billingEntityId: selectedCompany.company,
            professionalId: selectedProfessional.id,
            healthEntityId: selectedHealthEntity.id > 0 ? selectedHealthEntity.id : ALL,
            fromDate,
            toDate,
            formStates,
            paymentMethods,
        };
        setConfirmStep(false);
        reloadGrid(search);
    }

    /** Las filas con la misma clave se seleccionan y se quitan juntas. */
    function handleSelectionChange(nextPageSelection: PendingRow[]) {
        const nextRowKeys = new Set(nextPageSelection.map(r => r.rowKey));
        const prevRowKeys = new Set(selectedRows.map(r => r.rowKey));
        const next = new Map(selection);
        seenRowsRef.current.forEach(r => {
            const wasSelected = prevRowKeys.has(r.rowKey);
            const isSelected = nextRowKeys.has(r.rowKey);
            if (wasSelected && !isSelected) next.delete(r.movementKey);
            if (!wasSelected && isSelected) next.set(r.movementKey, toSelectedMovement(r));
        });
        setSelection(next);
    }

    function goToPayment(nextScope: SettlementScope, professionalAmount: Big | number) {
        setScope(nextScope);
        setPaymentMethodForOrder(null);
        setConfirmStep(true);
        const base = new Big(professionalAmount).round(2).toFixed(2);
        setPreviewBase(base);
        requestPreview(base, adjustments);
    }

    /**
     * El monto se precarga con lo que el cierre va a dejar pendiente: la parte del profesional más los créditos, menos
     * los débitos y su deuda. Se vuelve a pedir cada vez que cambian los ajustes (pisa el monto cargado a mano).
     */
    function requestPreview(base: string, rows: AdjustmentRow[]) {
        setPaymentAmount(null);
        setPaymentPreview(null);
        if (!appliedSearch) return;

        const requestId = ++previewRequestRef.current;
        setLoadingPreview(true);
        getSettlementPaymentPreview(appliedSearch.billingEntityId, appliedSearch.professionalId, base,
            adjustmentsTotal(rows, 'DEBIT'), adjustmentsTotal(rows, 'CREDIT'))
            .then(preview => {
                if (requestId !== previewRequestRef.current) return;
                setPaymentPreview(preview);
                setPaymentAmount(Number(preview.amountToPay));
            })
            .catch((error: unknown) => {
                if (requestId !== previewRequestRef.current) return;
                parseApiError(error);
                dispatch(setNotification({ title: 'No se pudo calcular el monto a pagar. Ingresalo a mano.', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            })
            .finally(() => {
                if (requestId === previewRequestRef.current) setLoadingPreview(false);
            });
    }

    function handleSettleAll() {
        if (!totals) return;
        goToPayment('all', totals.totalProfessionalAmount);
    }

    function handleSettleSelected() {
        const negatives = findUnselectedNegatives(new Set(selection.keys()), seenRowsRef.current.values());
        if (negatives.length > 0) {
            setUnselectedNegatives(negatives);
            return;
        }
        goToPayment('selected', selectedProfessionalTotal);
    }

    function handleAddNegatives() {
        const next = new Map(selection);
        unselectedNegatives.forEach(n => next.set(movementKey(n), toSelectedMovement(n)));
        setSelection(next);
        setUnselectedNegatives([]);
        const professionalTotal = sumMoney(
            Array.from(seenRowsRef.current.values()).filter(r => next.has(r.movementKey)).map(r => r.professionalAmount),
        );
        goToPayment('selected', professionalTotal);
    }

    function handleContinueWithoutNegatives() {
        setUnselectedNegatives([]);
        goToPayment('selected', selectedProfessionalTotal);
    }

    function handleAddAdjustment(adjustment: ManualAdjustmentDTO) {
        const next = [...adjustments, { ...adjustment, key: ++adjustmentKeyRef.current }];
        setAdjustments(next);
        setAdjustmentDialogType(null);
        if (previewBase != null) requestPreview(previewBase, next);
    }

    function handleRemoveAdjustment(row: AdjustmentRow) {
        const next = adjustments.filter(a => a.key !== row.key);
        setAdjustments(next);
        if (previewBase != null) requestPreview(previewBase, next);
    }

    async function handleClose() {
        if (!appliedSearch) return;
        if (paymentAmount === null || paymentAmount < 0) {
            dispatch(setNotification({ title: intl.formatMessage({ id: 'Error', defaultMessage: 'Ingresá el monto a pagar al profesional' }), icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            return;
        }
        if (!paymentMethodForOrder) {
            dispatch(setNotification({ title: intl.formatMessage({ id: 'Error', defaultMessage: 'Seleccioná el medio de pago para el profesional' }), icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            return;
        }
        // Sin saber si el sitio maneja cajas no se registra: podría quedar un egreso sin su movimiento de caja
        if (cashRegistersStatus !== 'loaded') return;
        if (requiresPaymentOrigin && !selectedCashRegister) {
            dispatch(setNotification({ title: 'Seleccioná la caja de origen', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            return;
        }
        if (requiresPaymentOrigin && !selectedFinancialAccount) {
            dispatch(setNotification({ title: 'Seleccioná la cuenta financiera de origen', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            return;
        }
        if (requiresPaymentOrigin && selectedFinancialAccountBalance == null) {
            dispatch(setNotification({ title: 'No se pudo consultar el saldo de la cuenta financiera', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            return;
        }
        if (paymentHasInsufficientFunds) {
            dispatch(setNotification({
                title: 'Saldo insuficiente en la cuenta financiera',
                desc: `Disponible: ${formatMoney(selectedFinancialAccountBalance)}. Requerido: ${formatMoney(paymentAmount)}.`,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        const register = async () => {
            setLoadingClose(true);
            try {
                await createProfessionalSettlement({
                    // Sin cajas en el sitio van null: el backend no registra movimientos de caja ni de cuenta
                    locationCashRegisterId: requiresPaymentOrigin ? selectedCashRegister.locationCashRegisterId : null,
                    billingEntityId: appliedSearch.billingEntityId,
                    professionalId: appliedSearch.professionalId,
                    fromDate: formatDateParam(appliedSearch.fromDate),
                    toDate: formatDateParam(appliedSearch.toDate, true),
                    paymentMethodIds: appliedSearch.paymentMethods,
                    formStateTypes: appliedSearch.formStates,
                    payerTypes: SETTLEMENT_PAYER_TYPES,
                    benefitOrderStates: SETTLEMENT_ORDER_STATES,
                    healthEntityId: appliedSearch.healthEntityId,
                    // Vacío = todo lo pendiente; el Map ya tiene cada clave una sola vez
                    selectedMovements: scope === 'all' ? [] : Array.from(selection.values()),
                    adjustments: adjustments.map(({ type, amount, description }) => ({ type, amount, description })),
                    // Las que vio el usuario: si cambiaron, el backend no cierra y se vuelve a calcular el pago
                    expectedRetentions: paymentPreview?.retentions ?? 0,
                    paymentAmount,
                    paymentOrderPaymentMethodId: paymentMethodForOrder.id,
                    paymentOrderFinancialAccountId: requiresPaymentOrigin ? selectedFinancialAccount.financialAccountId : null,
                });
                dispatch(setNotification({
                    title: intl.formatMessage({ id: 'ProfessionalSettlementDone', defaultMessage: 'Rendición profesional registrada exitosamente' }),
                    icon: 'pi-check-circle',
                    typeOfNotification: ETypeOfNotification.INFO,
                }));
                onSettlementCreated();
            } catch (error: unknown) {
                const apiError = parseApiError(error);
                if (apiError.errorCode === INSUFFICIENT_BALANCE) {
                    dispatch(setNotification({ title: 'Saldo insuficiente en la cuenta financiera.', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                } else if (apiError.errorCode === RETENTIONS_CHANGED) {
                    dispatch(setNotification({ title: apiError.message, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                    if (previewBase != null) requestPreview(previewBase, adjustments);
                } else if (NOT_PENDING_ERROR_CODES.includes(apiError.errorCode)) {
                    // Otro usuario rindió o pre-liquidó algún movimiento: se recarga la grilla con los mismos filtros
                    dispatch(setNotification({ title: 'Algunos ítems ya no están pendientes. Revisá la selección.', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                    setConfirmStep(false);
                    reloadGrid(appliedSearch);
                } else if (apiError.status >= 400 && apiError.status < 500) {
                    // Sesión de caja, comprobantes, negativos sin rendir…: el backend manda el motivo escrito para el usuario
                    dispatch(setNotification({ title: apiError.message, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                } else {
                    dispatch(setNotification({ title: 'No se pudo registrar la rendición. Intentá de nuevo.', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                }
            } finally {
                setLoadingClose(false);
            }
        };

        // Pagar de más está permitido: lo que sobra queda como pago de más y lo descuenta el próximo comprobante
        if (paymentPreview && new Big(paymentAmount).gt(paymentPreview.maxWithoutOverpayment)) {
            confirmAction({
                header: '¿Registrar un pago de más?',
                message: `El monto supera lo que se le debe (${formatMoney(paymentPreview.maxWithoutOverpayment)}). `
                    + 'Lo que sobra queda como pago de más y se descuenta en el próximo comprobante del profesional.',
                acceptLabel: 'Registrar pago',
                onAccept: register,
            });
            return;
        }
        await register();
    }

    return (
        <>
            {/* Header */}
            {/* Un solo « Volver (DS §3.7): en el paso de pago vuelve a la grilla; en la búsqueda, al historial */}
            <div className="new-settlement__heading">
                <div>
                    <Button
                        text
                        icon="pi pi-angle-double-left"
                        label={intl.formatMessage({ id: 'Back', defaultMessage: 'Volver' })}
                        className="md-back-link"
                        onClick={confirmStep ? () => setConfirmStep(false) : onClose}
                    />
                </div>
                <h1 className="md-page-title">Nueva rendición profesional</h1>
            </div>

            {/* La grilla queda montada (oculta) en el paso de pago: conserva lo cargado por el scroll y la selección */}
            <div style={{ display: confirmStep ? 'none' : undefined }}>
                <div className="new-settlement__filters">
                    <Combobox
                        label={intl.formatMessage({ id: 'BillingEntity', defaultMessage: 'Entidad Facturadora' })}
                        getItems={(p: GetItemsProps) => getAllCompanies(p)}
                        optionLabel="description"
                        value={selectedCompany}
                        setValue={setSelectedCompany}
                        style={{ width: 200 }}
                    />
                    <Combobox
                        label={intl.formatMessage({ id: 'Professional', defaultMessage: 'Profesional' })}
                        getItems={(p: GetItemsProps) => getProfessionals(p)}
                        optionLabel="name"
                        value={selectedProfessional}
                        setValue={setSelectedProfessional}
                        placeholder={intl.formatMessage({ id: 'Select', defaultMessage: 'Seleccionar' })}
                        style={{ width: 200 }}
                    />
                    <div style={{ width: 200 }}>
                        <InputDate
                            label={intl.formatMessage({ id: 'DateFrom', defaultMessage: 'Fecha desde' })}
                            value={fromDate}
                            onChange={(e: { value: Date | null }) => setFromDate(e.value)}
                        />
                    </div>
                    <div style={{ width: 200 }}>
                        <InputDate
                            label={intl.formatMessage({ id: 'DateTo', defaultMessage: 'Fecha hasta' })}
                            value={toDate}
                            onChange={(e: { value: Date | null }) => setToDate(e.value)}
                        />
                    </div>
                    <Combobox
                        label="Obra Social"
                        getItems={getHealthEntityComboOptionsWithAll}
                        optionLabel="name"
                        value={selectedHealthEntity}
                        setValue={(e: ComboDataDTO) => setSelectedHealthEntity(e ?? defaultAllComboDataDTO)}
                        placeholder="Todas"
                        style={{ width: 200 }}
                    />
                    <FilterMultiSelect label="Estado factura de la orden" value={formStates} options={FORM_STATE_OPTIONS} onChange={setFormStates} />
                    <FilterMultiSelect label="Medio de pago de la orden" value={paymentMethods} options={paymentMethodOptions} onChange={setPaymentMethods} />
                    <Button
                        outlined
                        className="md-ghost new-settlement__filter-action"
                        icon="pi pi-search"
                        label={intl.formatMessage({ id: 'Search', defaultMessage: 'Buscar' })}
                        onClick={handleSearch}
                    />
                </div>
                <p className="md-help new-settlement__scope-note">Incluye solo órdenes de paciente en estado Facturada.</p>

                {appliedSearch && (
                    <>
                        {totals && totals.totalCount > 0 && (
                            <div className="new-settlement__totals">
                                {(totals.totalsByPaymentMethod ?? []).map(t => (
                                    <div key={t.paymentMethod ?? 'none'} className="new-settlement__total-card">
                                        <span className="new-settlement__total-label">{t.paymentMethodName ?? 'Sin medio de pago'}</span>
                                        <span className="new-settlement__total-value"><SignedAmount amount={t.totalProfessionalAmount} format={formatMoney} /></span>
                                    </div>
                                ))}
                                <div className="new-settlement__total-card">
                                    <span className="new-settlement__total-label">
                                        {intl.formatMessage({ id: 'TotalProfessional', defaultMessage: 'Total Profesional' })}
                                    </span>
                                    <span className="new-settlement__total-value">
                                        <SignedAmount amount={totals.totalProfessionalAmount} format={formatMoney} />
                                    </span>
                                </div>
                            </div>
                        )}

                        <CustomTable
                            idTable="tableNewSettlementPending"
                            ref={tableRef}
                            getItems={getPendingRows}
                            pageSize={PAGE_SIZE}
                            selectionMode="checkbox"
                            selectedItems={selectedRows}
                            onSelectionChange={(e) => handleSelectionChange(e.value as PendingRow[])}
                            className="crudTable"
                            filterRow={false}
                            hideFiltersButton
                            stateless
                            notFlex
                            maxHeight={560}
                            notFoundText="No hay resultados para estos filtros."
                        >
                            <Column header="Movimiento" body={(r: PendingRow) => <MovementTypeTag movementType={r.movementType} detail={r.movementDetail} />} style={{ minWidth: 160 }} />
                            <Column header={intl.formatMessage({ id: 'Date', defaultMessage: 'Fecha' })} body={(r: PendingRow) => formatDate(r.date)} style={{ width: 100 }} />
                            <Column header="Pagador" body={(r: PendingRow) => r.payerTypeName ?? '—'} style={{ width: 110 }} />
                            <Column header={intl.formatMessage({ id: 'Patient', defaultMessage: 'Paciente' })} body={(r: PendingRow) => r.patientName ?? '—'} />
                            <Column header="Nomenclador" body={(r: PendingRow) => r.nomenclatorDescription ?? '—'} />
                            <Column header="Tipo" body={(r: PendingRow) => r.amountTypeName ?? '—'} style={{ width: 100 }} />
                            <Column header="Estado factura orden" body={(r: PendingRow) => r.orderFormStateTypeName ?? '—'} style={{ width: 130 }} />
                            <Column header="Medio de pago orden" body={(r: PendingRow) => r.orderPaymentMethodName ?? '—'} style={{ width: 140 }} />
                            <Column header={intl.formatMessage({ id: 'Amount', defaultMessage: 'Importe' })} body={(r: PendingRow) => <SignedAmount amount={r.amount} format={formatMoney} />} style={{ width: 130 }} headerClassName="col-right" className="text-right" />
                            <Column header="Tipo distribución" body={(r: PendingRow) => getDistributionContractTypeDescription(r.distributionContractType)} style={{ width: 120 }} />
                            <Column header={intl.formatMessage({ id: 'HonorariumPct', defaultMessage: '% Hon.' })} body={formatHonorariumPercentage} style={{ width: 80 }} headerClassName="col-right" className="text-right" />
                            <Column header={intl.formatMessage({ id: 'ProfessionalAmount', defaultMessage: 'Parte Prof.' })} body={(r: PendingRow) => <SignedAmount amount={r.professionalAmount} format={formatMoney} strong />} style={{ width: 130 }} headerClassName="col-right" className="text-right" />
                        </CustomTable>

                        {totals && totals.totalCount > 0 && (
                            <div className="new-settlement__action-bar">
                                <span className="new-settlement__action-summary">
                                    {selection.size > 0
                                        ? <>{selectedRows.length} movimiento(s) seleccionado(s){' — '}<strong>{formatMoney(selectedProfessionalTotal)}</strong></>
                                        : <>{totals.totalCount} movimiento(s) pendiente(s){' — '}<strong>{formatMoney(totals.totalProfessionalAmount)}</strong></>}
                                </span>
                                <div className="flex gap-2">
                                    <Button outlined className="md-ghost" label="Rendir todo lo pendiente" onClick={handleSettleAll} />
                                    <Button
                                        className="md-primary"
                                        label="Rendir seleccionados"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        onClick={handleSettleSelected}
                                        disabled={selection.size === 0}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}

                <UnsettledNegativesDialog
                    negatives={unselectedNegatives}
                    onAdd={handleAddNegatives}
                    onContinue={handleContinueWithoutNegatives}
                    onCancel={() => setUnselectedNegatives([])}
                />
            </div>

            {confirmStep && (
                <>
                    <div className="new-settlement__scope">
                        <span>
                            {scope === 'all' ? 'Todo lo pendiente' : 'Movimientos seleccionados'}: <strong>{scopeRowCount}</strong> movimiento(s)
                        </span>
                        <span>{intl.formatMessage({ id: 'Total', defaultMessage: 'Total' })}: <strong>{formatMoney(scopeTotal)}</strong></span>
                    </div>

                    <section className="new-settlement__payment">
                        <div className="new-settlement__section-header">
                            <h3 className="md-section-title">Débitos y créditos</h3>
                            <div className="new-settlement__section-actions">
                                <Button outlined icon="pi pi-minus-circle" label="Agregar débito" className="md-ghost"
                                        onClick={() => setAdjustmentDialogType('DEBIT')} disabled={loadingClose} />
                                <Button outlined icon="pi pi-plus-circle" label="Agregar crédito" className="md-ghost"
                                        onClick={() => setAdjustmentDialogType('CREDIT')} disabled={loadingClose} />
                            </div>
                        </div>
                        <p className="md-help">
                            Los débitos son lo que el profesional le debe a la institución y se restan de lo que cobra; los créditos se suman.
                            Al agregar o quitar uno se recalcula el monto a pagar.
                        </p>
                        <DataTable value={adjustments} dataKey="key" className="crudTable" emptyMessage="Sin débitos ni créditos.">
                            <Column header="Tipo" body={(row: AdjustmentRow) => row.type === 'DEBIT' ? 'Débito' : 'Crédito'} style={{ width: '120px' }} />
                            <Column field="description" header="Descripción" />
                            <Column
                                header="Monto"
                                body={(row: AdjustmentRow) => <SignedAmount amount={row.type === 'DEBIT' ? -row.amount : row.amount} format={formatMoney} />}
                                className="text-right"
                                headerClassName="col-right"
                                style={{ width: '170px' }}
                            />
                            <Column
                                header=""
                                style={{ width: '64px' }}
                                className="text-center"
                                body={(row: AdjustmentRow) => (
                                    <Button
                                        text
                                        rounded
                                        icon="pi pi-times"
                                        className="md-icon-btn"
                                        aria-label={`Quitar ${row.description}`}
                                        tooltip="Quitar"
                                        tooltipOptions={{ position: 'left' }}
                                        disabled={loadingClose}
                                        onClick={() => handleRemoveAdjustment(row)}
                                    />
                                )}
                            />
                        </DataTable>
                    </section>

                    {paymentPreview && paymentPreview.retentionLines.length > 0 && (
                        <section className="new-settlement__payment">
                            <h3 className="md-section-title">Retenciones por facturación propia</h3>
                            <p className="md-help">
                                Lo que no le corresponde al profesional de las órdenes que facturó él, según su contrato de distribución.
                                Se descuenta de lo que cobra, como los débitos.
                            </p>
                            <RetentionLinesTable lines={paymentPreview.retentionLines} />
                        </section>
                    )}

                    <section className="new-settlement__payment">
                        <h3 className="md-section-title">Pago al profesional</h3>
                        {loadingPreview && (
                            <p className="md-help">Calculando el monto a pagar…</p>
                        )}
                        {/* Estado de la cuenta siempre a la vista, aunque sea 0: el monto precargado es lo que deja pendiente el cierre */}
                        {paymentPreview && (
                            <div className="md-kpi-row">
                                <Kpi label="Parte prof." value={formatMoney(paymentPreview.professionalAmount)} />
                                {paymentPreview.manualCredits > 0 && <Kpi label="Créditos" value={formatMoney(paymentPreview.manualCredits)} />}
                                {paymentPreview.manualDebits > 0 && <Kpi label="Débitos" value={formatMoney(paymentPreview.manualDebits)} />}
                                {paymentPreview.retentions !== 0 && (
                                    <Kpi
                                        label={paymentPreview.retentions > 0 ? 'Retenciones' : 'Devolución de retenciones'}
                                        value={formatMoney(new Big(paymentPreview.retentions).abs())}
                                        hint="Por facturación propia"
                                    />
                                )}
                                <Kpi label="Deuda C/C" value={formatMoney(paymentPreview.accountDebt)} hint="Se descuenta al cerrar" />
                                <Kpi label="Saldo a favor" value={formatMoney(paymentPreview.accountCredit)} hint="De otros comprobantes" />
                                <Kpi label="A pagar" value={formatMoney(paymentPreview.amountToPay)} />
                            </div>
                        )}
                        {paymentPreview && paymentPreview.debitsExcess > 0 && (
                            <p className="md-help">
                                <i className="pi pi-info-circle" aria-hidden="true" /> {paymentPreview.retentions > 0 ? 'Los débitos y retenciones' : 'Los débitos'} superan lo que cobra en {formatMoney(paymentPreview.debitsExcess)}.
                                Al cerrar, el excedente se descuenta del saldo a favor y lo que no alcance queda como deuda en su cuenta corriente.
                            </p>
                        )}
                        {/* De dónde sale la deuda que se descuenta: pagos de más, retenciones o gastos anteriores */}
                        {paymentPreview?.accountDebtLines?.length ? (
                            <>
                                <p className="md-help">
                                    <i className="pi pi-info-circle" aria-hidden="true" /> Deuda de cuenta corriente que se descuenta al cerrar:
                                </p>
                                <AccountDebtLines lines={paymentPreview.accountDebtLines} />
                            </>
                        ) : null}
                        <div className="new-settlement__payment-fields">
                            <div className="inputContainer flexible--column settlement-amount-field">
                                <label htmlFor="new-settlement-amount" className="label inputLabel">Monto a pagar *</label>
                                <InputNumber
                                    inputId="new-settlement-amount"
                                    value={paymentAmount}
                                    onValueChange={(e) => setPaymentAmount(e.value ?? null)}
                                    mode="currency" currency="ARS" locale="es-AR" minFractionDigits={2}
                                />
                            </div>
                            <PaymentOriginFields
                                origin={paymentOrigin}
                                paymentMethod={paymentMethodForOrder}
                                onPaymentMethodChange={setPaymentMethodForOrder}
                                accountWarning={paymentHasInsufficientFunds
                                    ? `Saldo insuficiente (${formatMoney(selectedFinancialAccountBalance)}). Elegí otra cuenta o ingresá un monto menor.`
                                    : null}
                                idPrefix="new-settlement"
                            />
                        </div>
                        <PaymentOriginNote origin={paymentOrigin} />
                        <div className="new-settlement__payment-actions">
                            <Button
                                label={intl.formatMessage({ id: 'RegisterPayment', defaultMessage: 'Registrar pago' })}
                                className="md-primary"
                                loading={loadingClose}
                                onClick={handleClose}
                                disabled={cashRegistersStatus !== 'loaded'
                                    || loadingPreview
                                    || missingOpenCashRegister
                                    || (requiresPaymentOrigin && (loadingSelectedFinancialAccountBalance || selectedFinancialAccountBalance == null))
                                    || paymentHasInsufficientFunds}
                                tooltip={missingOpenCashRegister ? NO_OPEN_CASH_REGISTER_MESSAGE : undefined}
                                tooltipOptions={{ position: 'top', showOnDisabled: true }}
                            />
                        </div>
                    </section>
                </>
            )}

            <ManualAdjustmentDialog
                visible={adjustmentDialogType != null}
                type={adjustmentDialogType ?? 'DEBIT'}
                onHide={() => setAdjustmentDialogType(null)}
                onSubmit={handleAddAdjustment}
            />
        </>
    );
}
